import React, { useState, useEffect, useRef } from 'react';
import {
  Drawer, Box, Typography, IconButton, TextField, Button,
  FormControl, Select, MenuItem, Grid, InputAdornment,
  Autocomplete, Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PriceRangeSlider, { PRICE_MIN, PRICE_MAX, DEFAULT_PRICE_MAX } from './PriceRangeSlider';
import InventoryKeyHighlighter from '../../../components/inventory/InventoryKeyHighlighter';
import InventoryOtherDetails from '../../../components/inventory/InventoryOtherDetails';
import { 
  SECTION_COLORS,
  CATEGORY_OPTIONS,
  AREA_UNIT_OPTIONS,
  DEFAULT_PROPERTY_TYPES_BY_CATEGORY,
  CONFIG_TAG_SUGGESTIONS,
  CONSTRUCTION_STATUS_OPTIONS,
  SectionHeader,
} from '../../../utils/inventory_helper';

// ─── Design tokens ────────────────────────────────────────────────────────────
const DRAWER_WIDTH = 520;

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: 12.5, height: 32, borderRadius: '6px',
    '& fieldset': { borderColor: SECTION_COLORS.border },
    '&:hover fieldset': { borderColor: '#B0B8C4' },
    '&.Mui-focused fieldset': { borderColor: '#2D6A4F', borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root': { fontSize: 12, top: -2 },
  '& .MuiInputLabel-shrink': { top: 0 },
  '& .MuiSelect-select': { fontSize: 12.5, paddingTop: '6px', paddingBottom: '6px' },
};
const fieldSxTall = {
  '& .MuiOutlinedInput-root': {
    fontSize: 13, borderRadius: '6px',
    '& fieldset': { borderColor: SECTION_COLORS.border },
    '&:hover fieldset': { borderColor: '#B0B8C4' },
    '&.Mui-focused fieldset': { borderColor: '#2D6A4F', borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root': { fontSize: 12.5 },
  '& .MuiInputBase-input::placeholder': { fontSize: 12, opacity: 0.55 },
};

const parseProjectCommission = (v) => {
  if (v == null || v === '') return '';
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/[^0-9.]/g, ''));
  return Number.isNaN(n) ? '' : n;
};

const getCategoryKey = (o) => (o && typeof o === 'object' ? (o.value ?? o.id ?? o.label ?? o.name ?? '') : String(o ?? ''));
const getPropertyTypeKey = (o) => (o && typeof o === 'object' ? (o.id ?? o.value ?? o.name ?? o.label ?? '') : String(o ?? ''));
function formatIndianPrice(val) {
  if (!val && val !== 0) return '';
  const n = Number(val);
  if (n >= 1_00_00_000) return `₹${(n / 1_00_00_000).toFixed(2).replace(/\.?0+$/, '')} Cr`;
  if (n >= 1_00_000) return `₹${(n / 1_00_000).toFixed(1).replace(/\.?0+$/, '')} L`;
  return `₹${n.toLocaleString('en-IN')}`;
}

const FieldLabel = ({ label, required, complete }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
    {complete && <CheckCircleOutlineIcon sx={{ fontSize: 13, color: SECTION_COLORS.green }} />}
    <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: complete ? SECTION_COLORS.green : SECTION_COLORS.text, lineHeight: 1 }}>
      {label}{required && !complete && <span style={{ color: '#DC2626', marginLeft: 2 }}>*</span>}
    </Typography>
  </Box>
);


const AddNewConfiguration = ({
  open, onClose, onSubmit,
  projectName = '',
  projectCommissionRate,
  projectPriceMin,
  projectPriceMax,
  categoryOptions = [],
  areaUnitOptions = [],
  propertyTypeOptions = [],
}) => {
  const categoryOpts = categoryOptions?.length ? categoryOptions : CATEGORY_OPTIONS;
  const areaUnitOpts = areaUnitOptions?.length ? areaUnitOptions : AREA_UNIT_OPTIONS;
  const defaultRate = parseProjectCommission(projectCommissionRate);
  const effectivePriceMin = PRICE_MIN;
  const effectivePriceMax = PRICE_MAX;
  const defaultPriceRange = [effectivePriceMin, Math.min(DEFAULT_PRICE_MAX, effectivePriceMax)];

  const [formData, setFormData] = useState({
    name: '',
    category: null,
    propertyType: null,
    status: 'under_construction',
    priceRange: defaultPriceRange,
    commissionRate: defaultRate !== '' ? String(defaultRate) : '',
    areaMin: '',
    areaMax: '',
    areaUnit: 'sqft',
    totalUnits: '',
    keyHighlights: [],
    tags: [],
    otherDetails: [{ label: '', value: '' }],
  });

  const [propertyTypesList, setPropertyTypesList] = useState([]);
  const [propertyTypeLoading, setPropertyTypeLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
    if (field === 'category') {
      setErrors((p) => ({ ...p, propertyType: null }));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: null,
      propertyType: null,
      status: 'under_construction',
      priceRange: defaultPriceRange,
      commissionRate: defaultRate !== '' ? String(defaultRate) : '',
      areaMin: '',
      areaMax: '',
      areaUnit: 'sqft',
      totalUnits: '',
      keyHighlights: [],
      tags: [],
      otherDetails: [{ label: '', value: '' }],
    });
    setPropertyTypesList([]);
    setErrors({});
  };

  useEffect(() => { if (open) resetForm(); }, [open]);

  const { category, name, commissionRate, priceRange, areaMin, areaMax, areaUnit, tags } = formData;

  const complete = {
    identity: !!(name || '').trim(),
    type: !!(formData.category != null && formData.propertyType != null),
    price: Array.isArray(priceRange) && priceRange[0] < priceRange[1],
    commission: !!(commissionRate ?? '').toString().trim() && !isNaN(parseFloat(commissionRate)),
    area: !!(areaMin || areaMax),
  };
  const requiredComplete = complete.identity && complete.type && complete.price && complete.commission;
  const completedRequired = [complete.identity, complete.type, complete.price, complete.commission].filter(Boolean).length;

  const categoryKey = (() => {
    if (category == null) return null;
    if (typeof category === 'string') return String(category).toLowerCase().replace(/\s+/g, '_') || null;
    const v = category.value ?? category.id ?? category.label ?? category.name ?? '';
    return String(v).toLowerCase().replace(/\s+/g, '_') || null;
  })();
  const lastCategoryKeyRef = useRef(null);
  const lastAppliedCategoryKeyRef = useRef(null);
  const lastCategorySelectionTimeRef = useRef(0);
  const propertyTypeOptionsRef = useRef(propertyTypeOptions);
  propertyTypeOptionsRef.current = propertyTypeOptions;
  const propertyTypeOptionsLength = propertyTypeOptions?.length ?? 0;

  const getPtCategoryKey = (pt) => {
    if (!pt) return null;
    const c = pt.category;
    if (c && typeof c === 'object') return String(c.value ?? c.id ?? c.label ?? c.name ?? '').toLowerCase().replace(/\s+/g, '_');
    const name = pt.category_name ?? pt.category ?? '';
    return String(name).toLowerCase().replace(/\s+/g, '_');
  };

  useEffect(() => {
    if (!open) return;
    const k = categoryKey != null ? String(categoryKey) : null;
    if (lastCategoryKeyRef.current !== k) {
      lastCategoryKeyRef.current = k;
      lastAppliedCategoryKeyRef.current = null;
      setFormData((prev) => ({ ...prev, propertyType: null }));
      setErrors((p) => ({ ...p, propertyType: null }));
    }
  }, [open, categoryKey]);

  useEffect(() => {
    if (!open || !categoryKey) {
      setPropertyTypesList([]);
      setPropertyTypeLoading(false);
      setFormData((prev) => ({ ...prev, propertyType: null }));
      lastAppliedCategoryKeyRef.current = null;
      return;
    }
    const normKey = String(categoryKey).toLowerCase().replace(/\s+/g, '_');
    const selectedCategory = category && typeof category === 'object' ? category : null;
    const unitTypesFromCategory = selectedCategory?.unit_types;
    if (Array.isArray(unitTypesFromCategory)) {
      setPropertyTypeLoading(false);
      setPropertyTypesList(unitTypesFromCategory);
      if (lastAppliedCategoryKeyRef.current !== categoryKey) {
        lastAppliedCategoryKeyRef.current = categoryKey;
        setFormData((prev) => ({ ...prev, propertyType: unitTypesFromCategory.length > 0 ? unitTypesFromCategory[0] : null }));
        setErrors((p) => ({ ...p, propertyType: null }));
      }
      return;
    }
    const options = propertyTypeOptionsRef.current;
    const filtered = options?.length ? options.filter((pt) => getPtCategoryKey(pt) === normKey) : null;
    const list = Array.isArray(filtered) ? filtered : (DEFAULT_PROPERTY_TYPES_BY_CATEGORY[normKey] || []);
    setPropertyTypeLoading(false);
    setPropertyTypesList(list);
    if (lastAppliedCategoryKeyRef.current !== categoryKey) {
      lastAppliedCategoryKeyRef.current = categoryKey;
      setFormData((prev) => ({ ...prev, propertyType: list.length > 0 ? list[0] : null }));
      setErrors((p) => ({ ...p, propertyType: null }));
    }
  }, [open, categoryKey, category, propertyTypeOptionsLength]);

  const isCategorySelected = Boolean(
    categoryKey && String(categoryKey).trim() !== ''
  );

  const validate = () => {
    const data = formData;
    const next = {};
    if (!(data.name || '').trim()) next.name = 'Required';
    if (data.category == null || (typeof data.category === 'string' && !data.category.trim()))
      next.category = 'Category is required';
    if (data.propertyType == null || (typeof data.propertyType === 'string' && !data.propertyType.trim()))
      next.propertyType = 'Property type is required';
    const r = data.commissionRate;
    if (!r || String(r).trim() === '' || isNaN(parseFloat(r)))
      next.commissionRate = 'Commission rate is required';
    const pr = data.priceRange;
    if (Array.isArray(pr) && pr[0] >= pr[1]) next.priceRange = 'Max price must be greater than min';
    if (data.areaMin && data.areaMax && parseFloat(data.areaMin) >= parseFloat(data.areaMax))
      next.areaMax = 'Max must be greater than min';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    const data = formData;
    const catId = data.category?.value ?? data.category?.id ?? null;
    const ptId = data.propertyType?.id ?? data.propertyType?.value ?? null;
    const catName = typeof data.category === 'string' ? data.category : (data.category?.label ?? data.category?.name ?? '');
    const ptName = typeof data.propertyType === 'string' ? data.propertyType : (data.propertyType?.name ?? data.propertyType?.label ?? '');
    const totalUnitsVal = data.totalUnits !== '' && data.totalUnits != null && !Number.isNaN(Number(data.totalUnits))
      ? Number(data.totalUnits) : null;
    const otherDetailsRows = Array.isArray(data.otherDetails) ? data.otherDetails : [];
    const descriptionFromOther = otherDetailsRows
      .filter((r) => (r?.label ?? '').trim() || (r?.value ?? '').trim())
      .map((r) => `${(r.label || '').trim()}: ${(r.value || '').trim()}`)
      .join('\n');
    // Primary model fields at top level; rest in metadata (Configuration model)
    const payload = {
      category_id: catId,
      category_name: catName || undefined,
      property_type_id: ptId,
      property_type_name: ptId != null ? ptName : (catName || ptName || undefined),
      name: (data.name || '').trim(),
      description: descriptionFromOther || '',
      construction_status: data.status || 'under_construction',
      commission_rate: parseFloat(data.commissionRate),
      price_min: data.priceRange?.[0] ?? null,
      price_max: data.priceRange?.[1] ?? null,
      area_min: data.areaMin ? parseFloat(data.areaMin) : null,
      area_max: data.areaMax ? parseFloat(data.areaMax) : null,
      area_unit: data.areaUnit || 'sqft',
      tags: (data.tags || []).map((t) => String(t).trim()).filter(Boolean),
      key_highlights: Array.isArray(data.keyHighlights) ? data.keyHighlights.map((s) => String(s).trim()).filter(Boolean) : [],
      metadata: {
        ...(totalUnitsVal != null ? { total_units: totalUnitsVal } : {}),
        ...(otherDetailsRows.length > 0 ? { other_details: otherDetailsRows } : {}),
      },
    };
    setSubmitting(true);
    try {
      await onSubmit?.(payload);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const onFormSubmit = (e) => {
    e.preventDefault();
    if (validate()) handleSubmit();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: DRAWER_WIDTH },
          maxWidth: '100%',
          boxShadow: '-8px 0 40px rgba(0,0,0,0.14)',
          bgcolor: '#F0F2F5',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${SECTION_COLORS.border}`, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
              <Box sx={{ width: 4, height: 20, bgcolor: SECTION_COLORS.green, borderRadius: 1 }} />
              <Typography fontWeight={800} sx={{ color: SECTION_COLORS.text, letterSpacing: '-0.3px', fontSize: '1.05rem' }}>
                New Configuration
              </Typography>
            </Box>
            {projectName && (
              <Typography sx={{ fontSize: 11.5, color: SECTION_COLORS.sub, pl: 1.5 }}>{projectName}</Typography>
            )}
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: SECTION_COLORS.sub }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <form
        noValidate
        onSubmit={onFormSubmit}
        style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}
      >
        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', px: 2.5, py: 2 }}>
          <Box sx={{
            bgcolor: '#fff', borderRadius: '10px', border: `1px solid ${SECTION_COLORS.border}`,
            boxShadow: '0 1px 4px rgba(0,0,0,0.05)', p: 2,
          }}>
            {/* Section: Basic details */}
            <Box sx={{ mb: 2.5 }}>
              <SectionHeader title="Basic details" subtitle="Name, unit type and construction status" accent="info" complete={complete.identity && complete.type} />
              <Box sx={{ pl: 4.5 }}>
                <Box sx={{ mb: 2 }}>
                  <FieldLabel label="Configuration Name" required complete={complete.identity} />
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="e.g. 2BHK Premium, Garden Villa"
                    value={formData.name ?? ''}
                    onChange={(e) => handleChange('name', e.target.value)}
                    error={!!errors.name}
                    helperText={errors.name}
                    sx={fieldSxTall}
                    autoFocus
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <FieldLabel label="Category" required complete={complete.type} />
                  <Grid container spacing={1.25}>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        freeSolo
                        options={categoryOpts}
                        value={formData.category ?? null}
                        isOptionEqualToValue={(option, val) => getCategoryKey(option) === getCategoryKey(val) || (typeof option === 'string' && option === val) || (typeof val === 'string' && (option?.label ?? option?.name ?? '') === val)}
                        onChange={(_, v) => {
                          handleChange('category', v ?? null);
                          handleChange('propertyType', null);
                          setErrors((p) => ({ ...p, category: null, propertyType: null }));
                          if (v != null) lastCategorySelectionTimeRef.current = Date.now();
                        }}
                        onInputChange={(_, iv, reason) => {
                          if (reason !== 'input') return;
                          const isClearing = (iv ?? '').toString().trim() === '';
                          const hadSelection = formData.category != null && typeof formData.category === 'object';
                          const justSelected = hadSelection && (Date.now() - lastCategorySelectionTimeRef.current < 150);
                          if (isClearing && justSelected) return;
                          handleChange('category', iv || null);
                          handleChange('propertyType', null);
                        }}
                        getOptionLabel={(o) => typeof o === 'string' ? o : (o?.label ?? o?.name ?? '')}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Select or type to add category *" size="small"
                            error={!!errors.category} helperText={errors.category} sx={fieldSxTall} />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        freeSolo
                        disabled={!isCategorySelected || propertyTypeLoading}
                        options={propertyTypesList}
                        value={formData.propertyType ?? null}
                        isOptionEqualToValue={(option, val) => getPropertyTypeKey(option) === getPropertyTypeKey(val) || (typeof option === 'string' && option === val) || (typeof val === 'string' && (option?.name ?? option?.label ?? '') === val)}
                        onChange={(_, v) => handleChange('propertyType', v ?? null)}
                        onInputChange={(_, iv, reason) => { if (reason === 'input') handleChange('propertyType', iv || null); }}
                        getOptionLabel={(o) => typeof o === 'string' ? o : (o?.name ?? o?.label ?? '')}
                        renderInput={(params) => (
                          <TextField {...params}
                            placeholder='Select property type'
                            size="small"
                            error={!!errors.propertyType}
                            helperText={errors.propertyType}
                            sx={fieldSxTall}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  <Typography sx={{ fontSize: 10.5, color: SECTION_COLORS.sub, mt: 0.5 }}>
                    Select from the list or type to add a new category and unit type.
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <FieldLabel label="Construction Status" />
                  <FormControl fullWidth size="small" sx={fieldSx}>
                    <Select value={formData.status || 'under_construction'} onChange={(e) => handleChange('status', e.target.value)} displayEmpty>
                      {CONSTRUCTION_STATUS_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 12.5 }}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Box>

            {/* Section: Price & Commission */}
            <Box sx={{ mb: 2.5 }}>
              <SectionHeader title="Price & Commission" subtitle="Price range and commission rate" accent="pricing" complete={complete.price && complete.commission} />
              <Box sx={{ pl: 4.5 }}>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                    <FieldLabel label="Price Range" required complete={complete.price} />
                    {complete.price && (
                      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: SECTION_COLORS.amber }}>
                        {formatIndianPrice(priceRange?.[0])} – {formatIndianPrice(priceRange?.[1])}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ border: `1px solid ${errors.priceRange ? '#DC2626' : `${SECTION_COLORS.green}28`}`, borderRadius: '8px', p: 1.5, bgcolor: SECTION_COLORS.greenLight }}>
                    <PriceRangeSlider
                      value={formData.priceRange ?? defaultPriceRange}
                      onChange={(v) => handleChange('priceRange', v)}
                      error={errors.priceRange}
                      min={effectivePriceMin}
                      max={effectivePriceMax}
                    />
                  </Box>
                  {errors.priceRange && <Typography sx={{ fontSize: 11, color: '#DC2626', mt: 0.5 }}>{errors.priceRange}</Typography>}
                </Box>
                <Box sx={{ mb: 2 }}>
                  <FieldLabel label="Commission Rate" required complete={complete.commission} />
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={formData.commissionRate ?? ''}
                      onChange={(e) => handleChange('commissionRate', e.target.value)}
                      error={!!errors.commissionRate}
                      helperText={errors.commissionRate}
                      placeholder="e.g. 2"
                      sx={{ ...fieldSxTall, width: 160 }}
                      inputProps={{ min: 0, step: 0.1 }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: 13, color: SECTION_COLORS.sub }}>%</Typography></InputAdornment>,
                      }}
                    />
                    {formData.commissionRate && !isNaN(parseFloat(formData.commissionRate)) && (
                      <Box sx={{ px: 1.25, py: 0.75, bgcolor: SECTION_COLORS.purpleLight, borderRadius: '8px', border: `1px solid ${SECTION_COLORS.purple}22` }}>
                        <Typography sx={{ fontSize: '0.72rem', color: SECTION_COLORS.purple, fontWeight: 700 }}>
                          {parseFloat(formData.commissionRate)}% per unit
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Section: Area & Units */}
            <Box sx={{ mb: 2.5 }}>
              <SectionHeader title="Area & Units" subtitle="Area range and total units" accent="info" complete={complete.area} />
              <Box sx={{ pl: 4.5 }}>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                    <FieldLabel label="Area Range" complete={complete.area} />
                {complete.area && areaMin && areaMax && (
                    <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: SECTION_COLORS.teal }}>
                      {parseFloat(areaMin).toLocaleString('en-IN')} – {parseFloat(areaMax).toLocaleString('en-IN')}{' '}
                      {areaUnitOpts.find((o) => o.value === areaUnit)?.label ?? areaUnit}
                    </Typography>
                  )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <Box sx={{ flex: '1 1 100px' }}>
                      <Typography sx={{ fontSize: 10.5, color: SECTION_COLORS.sub, fontWeight: 600, mb: 0.5 }}>Min</Typography>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        placeholder="e.g. 850"
                        value={formData.areaMin ?? ''}
                        onChange={(e) => { handleChange('areaMin', e.target.value); setErrors((p) => ({ ...p, areaMax: null })); }}
                        inputProps={{ min: 0 }}
                        sx={fieldSx}
                      />
                    </Box>
                    <Box sx={{ flex: '1 1 100px' }}>
                      <Typography sx={{ fontSize: 10.5, color: SECTION_COLORS.sub, fontWeight: 600, mb: 0.5 }}>Max</Typography>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        placeholder="e.g. 1200"
                        value={formData.areaMax ?? ''}
                        onChange={(e) => handleChange('areaMax', e.target.value)}
                        error={!!errors.areaMax}
                        helperText={errors.areaMax}
                        inputProps={{ min: 0 }}
                        sx={fieldSx}
                      />
                    </Box>
                    <Box sx={{ flex: '0 0 auto', minWidth: 88 }}>
                      <Typography sx={{ fontSize: 10.5, color: SECTION_COLORS.sub, fontWeight: 600, mb: 0.5 }}>Unit</Typography>
                      <FormControl fullWidth size="small" sx={fieldSx}>
                        <Select value={formData.areaUnit || 'sqft'} onChange={(e) => handleChange('areaUnit', e.target.value)} displayEmpty
                          renderValue={(v) => areaUnitOpts.find((o) => o.value === v)?.label ?? v}>
                          {areaUnitOpts.map((u) => (
                            <MenuItem key={u.value} value={u.value} sx={{ fontSize: 12.5 }}>{u.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <FieldLabel label="Total Units" />
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    placeholder="e.g. 24"
                    value={formData.totalUnits ?? ''}
                    onChange={(e) => handleChange('totalUnits', e.target.value)}
                    inputProps={{ min: 0 }}
                    sx={fieldSxTall}
                  />
                </Box>
              </Box>
            </Box>

            {/* Section: Highlights & Tags */}
            <Box sx={{ mb: 2.5 }}>
              <SectionHeader title="Highlights & Tags" subtitle="Key highlights and tags" accent="highlights" />
              <Box sx={{ pl: 4.5 }}>
                <Box sx={{ mb: 2 }}>
                  <FieldLabel label="Key Highlights" />
                  <InventoryKeyHighlighter
                    value={Array.isArray(formData.keyHighlights) && formData.keyHighlights.length > 0 ? formData.keyHighlights : ['']}
                    onChange={(v) => handleChange('keyHighlights', v)}
                    placeholder="e.g. best in class amenities"
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <FieldLabel label="Tags" />
                  <Autocomplete
                    multiple
                    freeSolo
                    options={CONFIG_TAG_SUGGESTIONS}
                    value={formData.tags || []}
                    onChange={(_, v) => handleChange('tags', v || [])}
                    getOptionLabel={(o) => typeof o === 'string' ? o : (o?.label ?? '')}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Type or pick a tag" size="small"
                        sx={{
                          ...fieldSxTall,
                          '& .MuiOutlinedInput-root': { ...fieldSxTall['& .MuiOutlinedInput-root'], height: 'auto', minHeight: 38, fontSize: 12 },
                        }} />
                    )}
                    renderTags={(vals, getTagProps) =>
                      vals.map((option, i) => (
                        <Chip {...getTagProps({ index: i })} key={option} label={option} size="small"
                          sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, bgcolor: 'rgba(0,105,92,0.10)', color: SECTION_COLORS.teal, border: 'none', '& .MuiChip-deleteIcon': { fontSize: 13 } }} />
                      ))
                    }
                  />
                </Box>
              </Box>
            </Box>

            {/* Section: Other Details */}
            <Box sx={{ mb: 2.5 }}>
              <SectionHeader title="Other Details" subtitle="Add any label and value (stored as metadata)" accent="other" />
              <Box sx={{ pl: 4.5 }}>
                <InventoryOtherDetails
                  value={Array.isArray(formData.otherDetails) && formData.otherDetails.length > 0 ? formData.otherDetails : [{ label: '', value: '' }]}
                  onChange={(v) => handleChange('otherDetails', v)}
                  showSectionHeader={false}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{
          px: 2.5, py: 1.75, flexShrink: 0, borderTop: `1px solid ${SECTION_COLORS.border}`, bgcolor: '#fff',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <Typography sx={{ fontSize: 11.5, color: SECTION_COLORS.sub, fontWeight: 500 }}>
            {requiredComplete ? '✓ Required fields complete' : `${4 - completedRequired} required ${4 - completedRequired === 1 ? 'field' : 'fields'} remaining`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              type="button"
              onClick={onClose}
              sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12, color: '#6B7280', border: '1.5px solid #E4E7EC', borderRadius: '5px', px: '14px', height: 30, bgcolor: '#fff', '&:hover': { borderColor: '#9CA3AF', bgcolor: 'transparent' } }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              endIcon={<ArrowForwardIcon sx={{ fontSize: '15px !important' }} />}
              sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12, color: '#fff', bgcolor: SECTION_COLORS.green, border: 'none', borderRadius: '5px', px: '14px', height: 30, boxShadow: 'none', '&:hover': { bgcolor: '#1b5e20', boxShadow: 'none' }, '&:disabled': { opacity: 0.6 } }}
            >
              {submitting ? 'Creating…' : 'Create Configuration'}
            </Button>
          </Box>
        </Box>
      </form>
    </Drawer>
  );
};

export default AddNewConfiguration;
